import api from "../api";

function buildMailFilterParams(filters = {}) {
  const params = {};
  if (filters.responded != null) params.responded = filters.responded;
  if (filters.status != null) params.status = filters.status;
  if (filters.rejected != null) params.rejected = filters.rejected;
  if (filters.responsibleId != null) params.responsible_id = filters.responsibleId;
  return params;
}

export const mailsService = {
  sendToReview: async (mailsToSendData) => {
    const res = await api.post("/api/mail_to_send/add-mail/", mailsToSendData);
    return res.data;
  },

  fetchMails: async ({ filters = {} } = {}) => {
    const res = await api.get("/api/mails/mails_list/", {
      params: buildMailFilterParams(filters),
    });
    return res.data;
  },

  fetchMailsSummary: async ({ filters = {} } = {}) => {
    const res = await api.get("/api/mails/mails_list_summary/", {
      params: buildMailFilterParams(filters),
    });
    return res.data;
  },

  sendGroupsToReview: async (
    templateGroupIds,
    filters = {},
    { onlyLateOrUnsent = false } = {},
  ) => {
    const res = await api.post("/api/mail_to_send/add-by-template-groups/", {
      template_group_ids: templateGroupIds,
      only_late_or_unsent: onlyLateOrUnsent,
      ...buildMailFilterParams(filters),
    });
    return res.data;
  },

  deleteCommunicationsByGroups: async (templateGroupIds, filters = {}) => {
    const res = await api.post("/api/mails/delete-by-template-groups/", {
      template_group_ids: templateGroupIds,
      ...buildMailFilterParams(filters),
    });
    return res.data;
  },

  fetchMailsToSend: async (params = {}) => {
    const res = await api.get("/api/mail_to_send/by-user/", { params });
    return res.data;
  },

  approveMailToSend: async (id) => {
    const res = await api.post(`/api/mail_to_send/${id}/approve/`);
    return res.data;
  },

  rejectMailToSend: async (id) => {
    const res = await api.post(`/api/mail_to_send/${id}/reject/`);
    return res.data;
  },

  fetchTemplateGroupMails: async (
    templateGroupId,
    { page = 1, search = "", filters = {} } = {},
  ) => {
    const params = { page, ...buildMailFilterParams(filters) };
    if (search.trim()) params.search = search.trim();

    const res = await api.get(
      `/api/mails/template-group-mails/${templateGroupId}/`,
      { params },
    );
    return res.data;
  },

  fetchSendErrors: async ({ page = 1, search = "", filters = {} } = {}) => {
    const params = { page, ...buildMailFilterParams(filters) };
    if (search.trim()) params.search = search.trim();

    const res = await api.get("/api/mails/send-errors/", { params });
    return res.data;
  },

  fetchTemplateGroupMailsSummary: async (
    templateGroupId,
    { page = 1, search = "", filters = {} } = {},
  ) => {
    const params = { page, ...buildMailFilterParams(filters) };
    if (search.trim()) params.search = search.trim();

    const res = await api.get(
      `/api/mails/template-group-mails/${templateGroupId}/summary/`,
      { params },
    );
    return res.data;
  },

  fetchMail: async (mailId) => {
    const res = await api.get(`/api/mails/${mailId}/`);
    return res.data;
  },

  fetchMailRendered: async (mailId) => {
    const res = await api.get(`/api/mails/${mailId}/rendered/`);
    return res.data;
  },

  deleteMail: async (mailId) => {
    await api.delete(`/api/mails/${mailId}/`);
  },

  updateMail: async (mailId, changes) => {
    const res = await api.patch(`/api/mails/${mailId}/`, changes);
    return res.data;
  },

  reGetTemplate: async (mailIds) => {
    const res = await api.post("/api/mails/re-get-template/", { ids: mailIds });
    return res.data;
  },

  enableMails: async (mailIds) => {
    const res = await api.post("/api/mails/enable/", { ids: mailIds });
    return res.data;
  },

  createMailApi: async (mailData) => {
    const res = await api.post("/api/mails/", mailData);
    return res.data;
  },

  fetchCommunicationSubjects: async (params = {}) => {
    const res = await api.get("/api/mails/communication-subjects/", { params });
    return res.data;
  },

  markConversationRead: async ({ leadId, subjectGroupId }) => {
    const res = await api.post("/api/mails/conversation/mark-read/", {
      lead_id: leadId,
      subject_group_id: subjectGroupId,
    });
    return res.data;
  },

  fetchLeadConversation: async (params = {}) => {
    const res = await api.get("/api/mails/conversation/", { params });
    return res.data;
  },

  setConversationReminder: async ({
    leadId,
    subjectGroupId,
    date,
    hour,
    timezone,
  }) => {
    const res = await api.put("/api/mails/conversation/reminder/", {
      lead_id: leadId,
      subject_group_id: subjectGroupId,
      reminder_date: date,
      reminder_hour: hour,
      ...(timezone ? { timezone } : {}),
    });
    return res.data;
  },

  clearConversationReminder: async ({ leadId, subjectGroupId, reminderId }) => {
    const res = await api.delete("/api/mails/conversation/reminder/", {
      params: {
        lead_id: leadId,
        subject_group_id: subjectGroupId,
        ...(reminderId ? { reminder_id: reminderId } : {}),
      },
    });
    return res.data;
  },

  sendMailReply: async (payload) => {
    const res = await api.post("/api/mails/mail-replies/", payload);
    return res.data;
  },

  sendLeadNewMail: async ({ leadId, payload }) => {
    const res = await api.post(`/api/mails/leads/${leadId}/new-mail/`, payload);
    return res.data;
  },

  syncReplies: async () => {
    const res = await api.post("/api/mails/mailcorps/sync-replies/", {
      mark_seen: true,
      limit_per_mailcorp: 50,
    });
    return res.data;
  },

  restartChain: async (mailId) => {
    const res = await api.post(`/api/mails/${mailId}/restart-chain/`);
    return res.data;
  },

  fetchUnassignedMails: async ({
    status = "pending",
    search = "",
    limit = 50,
    offset = 0,
  } = {}) => {
    const params = { status, limit, offset };
    if (search.trim()) params.search = search.trim();

    const res = await api.get("/api/mails/unassigned/", { params });
    return res.data;
  },

  fetchInboundAttachmentLink: async (attachmentId, { variant } = {}) => {
    const url = `/api/mails/inbound-attachments/${attachmentId}/link/`;
    const res = variant
      ? await api.get(url, { params: { variant } })
      : await api.get(url);
    return res.data.url;
  },

  fetchUnassignedMail: async (unassignedMailId) => {
    const res = await api.get(`/api/mails/unassigned/${unassignedMailId}/`);
    return res.data;
  },

  ignoreUnassignedMail: async (unassignedMailId) => {
    const res = await api.post(
      `/api/mails/unassigned/${unassignedMailId}/ignore/`,
    );
    return res.data;
  },

  restoreUnassignedMail: async (unassignedMailId) => {
    const res = await api.post(
      `/api/mails/unassigned/${unassignedMailId}/restore/`,
    );
    return res.data;
  },

  markUnassignedMailAsSpam: async (unassignedMailId) => {
    const res = await api.post(
      `/api/mails/unassigned/${unassignedMailId}/spam/`,
    );
    return res.data;
  },

  assignUnassignedMail: async (unassignedMailId, { leadId, personIds = [] }) => {
    const res = await api.post(
      `/api/mails/unassigned/${unassignedMailId}/assign/`,
      { lead_id: leadId, person_ids: personIds },
    );
    return res.data;
  },

  deleteUnassignedMail: async (unassignedMailId) => {
    await api.delete(`/api/mails/unassigned/${unassignedMailId}/`);
  },
};
